/**
 * W-7 发布商品(创建 + 编辑)。视觉真源 design/publish.html;业务真源 App PublishPage.tsx。
 * 7 段表单 + 字段级内联校验 + 提交守卫链(注册→KYC→昵称→卖家须知)+ 封面延迟直传 + resp.status 三分支。
 * 进屏 KYC 门禁由 router 的 <KycGate> 包裹(未实名不渲染本表单)。
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getCategoryTree } from '@/api/category';
import { getMyProfile } from '@/api/profile';
import { getSellerNoticeStatus, acceptSellerNotice } from '@/api/sellerNotice';
import {
  editProduct,
  getSellerProduct,
  publishProduct,
  type ProductMedium,
  type ProductUnit,
} from '@/api/products';
import { ServerError } from '@/api/client';
import { useAuthStore } from '@/store/auth';
import { isKycVerified } from '@/lib/identity';
import { uploadCover } from '@/lib/coverUpload';
import { queryClient } from '@/lib/queryClient';
import { MEDIUM_OPTIONS, UNIT_OPTIONS } from '@/lib/productLabels';
import { toast } from '@/components/ui/toast';
import { validatePublishFields, type PublishErrors, type PublishField } from './validate';
import { CoverImageGrid, type CoverItem } from './CoverImageGrid';
import { SellerNoticeDialog } from './SellerNoticeDialog';

let coverSeq = 0;
const nextCoverKey = () => `cover-${(coverSeq += 1)}`;
const truncate = (s: string | null | undefined, n: number): string =>
  s ? [...s].slice(0, n).join('') : '';

/** 字段级错误提示(输入框正下方红字)。 */
function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null;
  return (
    <p className="sm mt-1" role="alert" style={{ color: 'var(--color-destructive)' }}>
      {msg}
    </p>
  );
}

export function PublishPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const identityState = useAuthStore((s) => s.identityState);
  const editProductId = params.get('mode') === 'edit' ? params.get('productId') : null;
  const isEdit = !!editProductId;

  const [covers, setCovers] = useState<CoverItem[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [unit, setUnit] = useState<ProductUnit | null>(null);
  const [medium, setMedium] = useState<ProductMedium>('IM'); // 唯一有默认
  const [categoryL1Id, setCategoryL1Id] = useState<string | null>(null);
  const [categoryL2Id, setCategoryL2Id] = useState<string | null>(null);
  const [errors, setErrors] = useState<PublishErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [noticeVersion, setNoticeVersion] = useState<string | undefined>();
  const [noticeSubmitting, setNoticeSubmitting] = useState(false);
  const [busy, setBusy] = useState(false); // 覆盖整条守卫链(含 doPublish),防重入
  const submitLock = useRef(false);
  const coversRef = useRef<CoverItem[]>([]);
  coversRef.current = covers;

  // 用户开始修改某字段即清除其内联错误
  const clearErr = (f: PublishField) =>
    setErrors((e) => (e[f] ? { ...e, [f]: undefined } : e));

  // 卸载时回收封面本地预览 objectURL(仅 File 来源)
  useEffect(
    () => () => {
      coversRef.current.forEach((c) => {
        if (c.file) URL.revokeObjectURL(c.previewUrl);
      });
    },
    [],
  );

  const categoryQuery = useQuery({ queryKey: ['categoryTree'], queryFn: getCategoryTree });
  const profileQuery = useQuery({ queryKey: ['myProfile'], queryFn: getMyProfile });
  const detailQuery = useQuery({
    queryKey: ['sellerProduct', editProductId],
    queryFn: () => getSellerProduct(editProductId as string),
    enabled: isEdit,
  });

  // 编辑预填
  useEffect(() => {
    const d = detailQuery.data;
    if (!d) return;
    setTitle(d.title);
    setDescription(d.description);
    setPrice(String(Math.round(d.price / 100)));
    setUnit(d.unit);
    setMedium(d.medium);
    setCategoryL1Id(d.categoryL1Id);
    setCategoryL2Id(d.categoryL2Id);
    setCovers(
      (d.coverImages ?? []).map((u) => ({ key: nextCoverKey(), previewUrl: u, remoteUrl: u })),
    );
  }, [detailQuery.data]);

  // 编辑预填失败 → 提示回退
  useEffect(() => {
    if (isEdit && detailQuery.isError) {
      toast.error(toMessage(detailQuery.error, '无法编辑该商品'));
      navigate('/seller-products', { replace: true });
    }
  }, [isEdit, detailQuery.isError, detailQuery.error, navigate]);

  const l1Items = categoryQuery.data ?? [];
  const l2Items = l1Items.find((n) => n.id === categoryL1Id)?.children ?? [];

  const onAddCovers = (files: File[]) => {
    clearErr('cover');
    setCovers((prev) => [
      ...prev,
      ...files.map((file) => ({ key: nextCoverKey(), previewUrl: URL.createObjectURL(file), file })),
    ]);
  };
  const onRemoveCover = (key: string) =>
    setCovers((prev) => {
      const found = prev.find((c) => c.key === key);
      if (found?.file) URL.revokeObjectURL(found.previewUrl); // 删除即回收本地预览
      return prev.filter((c) => c.key !== key);
    });

  const onSelectL1 = (id: string) => {
    clearErr('category');
    setCategoryL1Id(id || null);
    setCategoryL2Id(null); // 换 L1 清 L2,避免跨类脏值
  };

  // ---- 提交守卫链 ----
  const handleSubmit = async () => {
    if (busy) return; // 防重入:覆盖异步守卫链(避免重复 getSellerNoticeStatus / 重复弹窗)
    setBusy(true);
    try {
      const fieldErrors = validatePublishFields({
        coverCount: covers.length,
        title,
        categoryL1Id,
        categoryL2Id,
        description,
        price,
        unit,
        medium,
      });
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
        return;
      }
      setErrors({});
      // KYC 兜底(进屏已由 KycGate 拦截,此为防御)
      if (!isKycVerified(identityState)) {
        navigate('/kyc-verify', { state: { from: '/publish' } });
        return;
      }
      // 昵称:profile 已加载且为空才拦(未加载不拦,同 App)。引导去「编辑资料」设置。
      const profile = profileQuery.data;
      if (profile && !profile.nickname?.trim()) {
        toast.info('发布前请先设置昵称');
        navigate('/profile/edit', { state: { from: '/publish' } });
        return;
      }
      // 卖家须知:required 才弹窗;status 网络错误吞掉不阻断(后端 020409 兜底)
      try {
        const status = await getSellerNoticeStatus();
        if (status.required) {
          setNoticeVersion(status.currentVersion);
          setNoticeOpen(true);
          return;
        }
      } catch {
        // swallow
      }
      await doPublish();
    } finally {
      setBusy(false);
    }
  };

  const onNoticeAccept = async () => {
    setNoticeSubmitting(true);
    try {
      await acceptSellerNotice();
      setNoticeOpen(false);
      await doPublish();
    } catch (err) {
      toast.error(toMessage(err, '提交失败,请稍后重试'));
    } finally {
      setNoticeSubmitting(false);
    }
  };

  const doPublish = async () => {
    if (submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    try {
      // 封面延迟直传:本地 File → presign PUT → finalUrl;既有远端 URL 复用
      const coverImages: string[] = [];
      for (const c of covers) {
        coverImages.push(c.file ? await uploadCover(c.file) : (c.remoteUrl ?? c.previewUrl));
      }
      const req = {
        title: title.trim(),
        description,
        price: Number(price) * 100,
        unit: unit as string,
        categoryL1Id: categoryL1Id as string,
        categoryL2Id: categoryL2Id as string,
        medium,
        coverImages,
      };
      const resp = isEdit
        ? await editProduct(editProductId as string, req)
        : await publishProduct(req);
      handleResp(resp.status, resp.rejectionReason);
    } catch (err) {
      toast.error(toMessage(err, isEdit ? '保存失败,请重试' : '发布失败,请重试'));
    } finally {
      setSubmitting(false);
      submitLock.current = false;
    }
  };

  const handleResp = (status: string | undefined, rejectionReason?: string) => {
    // 商品已入库(含 REJECTED)→ 一律跳「我的发布」,让用户编辑现有商品重提,
    // 避免留屏重复提交创建一堆驳回商品(创建态 publishProduct 每次新建)。
    void queryClient.invalidateQueries({ queryKey: ['sellerProducts'] });
    if (status === 'REJECTED') {
      toast.error(
        `${isEdit ? '审核未通过' : '发布被驳回'}:${truncate(rejectionReason, 50) || '内容不符合规范'},可在「我的发布」中修改后重新提交`,
      );
    } else if (status === 'ONLINE') {
      toast.success(isEdit ? '已重新上架' : '你的商品已上线');
    } else {
      toast.info('已提交,等待审核');
    }
    navigate('/seller-products');
  };

  if (isEdit && detailQuery.isLoading) {
    return (
      <div className="inner section">
        <div className="state">
          <div className="skeleton" style={{ width: 48, height: 48, borderRadius: '50%' }} />
          <p className="muted sm mt-4">加载商品信息…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="inner section">
      <p className="sm muted mb-6">首页 / {isEdit ? '编辑商品' : '发布商品'}</p>
      <div className="row between mb-8">
        <div>
          <h1 className="h1">{isEdit ? '编辑商品' : '上架我的商品'}</h1>
          <p className="muted mt-2">如实填写商品信息,提交后进入审核。</p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>
          取消
        </button>
      </div>

      <div className="detail-cols">
        <div>
          {/* 封面 */}
          <div className="card card-pad mb-6">
            <h3 className="h3 mb-4">商品封面</h3>
            <CoverImageGrid
              covers={covers}
              disabled={submitting}
              onAdd={onAddCovers}
              onRemove={onRemoveCover}
            />
            <FieldError msg={errors.cover} />
          </div>

          {/* 商品信息 */}
          <div className="card card-pad mb-6">
            <h3 className="h3 mb-4">商品信息</h3>

            <div className="field">
              <label className="label" htmlFor="pub-title">
                商品标题
              </label>
              <input
                id="pub-title"
                className="input"
                type="text"
                placeholder="一句话说清你的商品"
                maxLength={60}
                value={title}
                onChange={(e) => {
                  clearErr('title');
                  setTitle(e.target.value);
                }}
              />
              <p className="hint">最多 60 字。</p>
              <FieldError msg={errors.title} />
            </div>

            <div className="row gap-4">
              <div className="field" style={{ flex: 1 }}>
                <label className="label" htmlFor="pub-l1">
                  一级品类
                </label>
                <select
                  id="pub-l1"
                  className="select"
                  value={categoryL1Id ?? ''}
                  onChange={(e) => onSelectL1(e.target.value)}
                >
                  <option value="">
                    {categoryQuery.isError
                      ? '品类加载失败'
                      : l1Items.length
                        ? '请选择一级品类'
                        : '品类加载中…'}
                  </option>
                  {l1Items.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.name}
                    </option>
                  ))}
                </select>
                {categoryQuery.isError && (
                  <button
                    type="button"
                    className="btn btn-ghost sm mt-2"
                    onClick={() => categoryQuery.refetch()}
                  >
                    品类加载失败,点击重试
                  </button>
                )}
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label className="label" htmlFor="pub-l2">
                  二级品类
                </label>
                <select
                  id="pub-l2"
                  className="select"
                  value={categoryL2Id ?? ''}
                  disabled={!categoryL1Id}
                  onChange={(e) => {
                    clearErr('category');
                    setCategoryL2Id(e.target.value || null);
                  }}
                >
                  <option value="">
                    {!categoryL1Id
                      ? '请先选择一级品类'
                      : l2Items.length
                        ? '请选择二级品类'
                        : '该品类暂无二级选项'}
                  </option>
                  {l2Items.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <FieldError msg={errors.category} />

            <div className="field">
              <label className="label" htmlFor="pub-desc">
                商品介绍
              </label>
              <textarea
                id="pub-desc"
                className="textarea"
                style={{ minHeight: 160 }}
                placeholder="详细介绍商品内容、适合人群、价值"
                value={description}
                onChange={(e) => {
                  clearErr('description');
                  setDescription(e.target.value);
                }}
              />
              <p className="hint">{[...description].length}/2000(不少于 15 字)</p>
              <FieldError msg={errors.description} />
            </div>

            <div className="row gap-4" style={{ alignItems: 'flex-start' }}>
              <div className="field" style={{ flex: 2 }}>
                <label className="label" htmlFor="pub-price">
                  价格(元)
                </label>
                <input
                  id="pub-price"
                  className="input"
                  type="text"
                  inputMode="numeric"
                  placeholder="9 ~ 9999"
                  maxLength={4}
                  value={price}
                  onChange={(e) => {
                    clearErr('price');
                    setPrice(e.target.value.replace(/\D/g, ''));
                  }}
                />
                <p className="hint">以元录入,后端按分存储。</p>
                <FieldError msg={errors.price} />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label className="label" htmlFor="pub-unit">
                  计价单位
                </label>
                <select
                  id="pub-unit"
                  className="select"
                  value={unit ?? ''}
                  onChange={(e) => {
                    clearErr('unit');
                    setUnit((e.target.value || null) as ProductUnit | null);
                  }}
                >
                  <option value="">请选择</option>
                  {UNIT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <FieldError msg={errors.unit} />
              </div>
            </div>

            <div className="field" style={{ marginBottom: 0 }}>
              <label className="label">商品介质</label>
              <div className="row gap-2 wrap">
                {MEDIUM_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    className={medium === o.value ? 'tag active' : 'tag'}
                    onClick={() => {
                      clearErr('medium');
                      setMedium(o.value);
                    }}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <p className="hint">默认站内 IM。</p>
              <FieldError msg={errors.medium} />
            </div>
          </div>

          <div className="row gap-4">
            <button
              type="button"
              className="btn btn-primary btn-lg"
              disabled={submitting || busy}
              onClick={handleSubmit}
            >
              {submitting || busy ? '提交中…' : isEdit ? '保存修改' : '发布商品'}
            </button>
          </div>
          <p className="hint mt-4">提交后进入「审核中」,通过后自动上线;若被驳回可留在本页修改重提。</p>
        </div>

        {/* 右:发布须知 */}
        <aside className="sticky-cta">
          <div className="card card-pad mb-6">
            <h3 className="h3 mb-4">发布须知</h3>
            <ul className="muted sm" style={{ lineHeight: 2, paddingLeft: '1.1em' }}>
              <li>
                需先<b>实名认证</b>方可发布。
              </li>
              <li>如实描述商品,不得虚假宣传或诱导站外交易。</li>
              <li>
                提交即进入<b>审核</b>,通过后对买家可见。
              </li>
            </ul>
          </div>
        </aside>
      </div>

      <SellerNoticeDialog
        open={noticeOpen}
        currentVersion={noticeVersion}
        submitting={noticeSubmitting}
        onAccept={onNoticeAccept}
        onClose={() => setNoticeOpen(false)}
      />
    </div>
  );
}

function toMessage(err: unknown, fallback: string): string {
  if (err instanceof ServerError) return err.message || fallback;
  return fallback;
}
